import React, { useCallback, useEffect, useRef, useState } from "react";
import Card from "../../../elements/Card";
import Select from "../../../form/select/Select";
import DatePicker from "../../../form/DatePicker";
import TimePicker from "../../../form/timePicker/TimePicker";
import LocationSearch from "../../../elements/LocationSearch";
import { useParams } from "react-router-dom";
import useClickOutside from "../../../../hooks/useClickOutside";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../../../hooks/useDebounce";
import { countryList } from "../../../../services/countryService";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useSelector } from "react-redux";
import { classNames } from "../../../../helpers/classNames";

const ReadOnly = ({ data }) => {
  const { t } = useTranslation();
  return (
    <Card
      title={t("Schedule & Venue")}
      bodyClass={"space-y-6"}
      className={" py-4 md:py-8 px-3 md:px-6"}
    >
      <div className="grid grid-cols-12 divide-y md:divide-x">
        <div className="col-span-12 md:col-span-6 p-3 space-y-3">
          <div className="space-y-1">
            <h3 className="font-semibold text-lg">{t("Event Type")}</h3>
            <div className="text-text-gradient-red font-medium">
              {data?.locationStatus}
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="font-semibold text-lg">{t("Currency")}</h3>
            <div className="text-text-gradient-red font-medium">
              <div className="flex items-center gap-2">
                <div className="h-3 min-w-3 flex items-center justify-center bg-slate-100 rounded-md p-2 py-3">
                  {data?.currency?.symbol}
                </div>{" "}
                {data?.currency?.name} {`(${data?.currency?.code})`}
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="font-semibold text-lg">{t("Event Date")}</h3>
            <div className="text-text-gradient-red font-medium">
              {dayjs(data?.start_end_date?.startDate).format(
                "ddd, DD MMM YYYY hh:mm A"
              )}
              {" - "}
              {dayjs(data?.start_end_date?.endDate).format(
                "ddd, DD MMM YYYY hh:mm A"
              )}
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-6 p-3 space-y-3">
          <div className="space-y-1">
            <h3 className="font-semibold text-lg">{t("Location")}</h3>
            <div className="text-text-gradient-red font-medium">
              <div>{data?.address?.name}</div>
              <div>{data?.address?.state}</div>
              <div>{data?.address?.city}</div>
              <div>{data?.address?.country}</div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const Venue = ({
  values = {},
  handleChange = () => {},
  formValidate = () => {},
  setFieldsValue = () => {},
  errors,
  data,
}) => {
  const { t } = useTranslation();
  const divRef = useRef(null);
  const { eventId } = useParams();
  const [readOnly, setReadOnly] = useState(false);
  const [searchCurrency, setSearchCurrency] = useState("");
  const currency_search = useDebounce(searchCurrency, 300);

  const {
    data: countryData,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["country", currency_search],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await countryList({
        page: pageParam,
        limit: 10,
        groupBy: "currency",
        ...(currency_search && {
          keyword: currency_search,
        }),
      });
      return res?.docs || {};
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.metadata?.hasNextPage
        ? lastPage?.metadata?.nextPage
        : undefined;
    },
  });

  const polishedCountryList = useCallback(
    ({ list = [] }) => {
      if (
        list?.some(
          (item) =>
            item?._id === (values?.currency?.code || values?.currency?.value)
        )
      ) {
        return list;
      } else {
        if (values?.currency?.code || values?.currency?.value) {
          return [
            {
              currency_symbol: values?.currency?.symbol,
              currency_name: values?.currency?.name,
              _id: values?.currency?.code || values?.currency?.value,
            },
            ...list,
          ];
        } else {
          return list;
        }
      }
    },
    [JSON.stringify(values?.currency)]
  );

  useClickOutside(divRef, () => {
    if (eventId) {
      setReadOnly(true);
    } else {
      setReadOnly(false);
    }
  });

  useEffect(() => {
    if (eventId) {
      setReadOnly(true);
    }
  }, [eventId]);

  return (
    <div
      ref={divRef}
      onClick={() => {
        if (eventId) {
          setReadOnly(false);
        }
      }}
    >
      {readOnly ? (
        <ReadOnly data={values} />
      ) : (
        <Card title={t("Schedule & Venue")} bodyClass={"space-y-6"}>
          <div className="relative grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <Select
                label={t("Type of Event")}
                placeholder={t("Select event type")}
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                dropdownClass="w-full"
                dropdownButtonClass={
                  "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full"
                }
                dropdownData={[
                  { name: "Online", value: "online" },
                  { name: "Offline", value: "offline" },
                  // { name: "notspecified", value: "notspecified" },
                ]}
                onChange={(val) => {
                  handleChange({
                    target: { name: "locationStatus", value: val },
                  });
                }}
                selectedValue={values?.locationStatus}
                {...formValidate(errors, "locationStatus")}
              />
            </div>
            <div className="col-span-12 md:col-span-6">
              <Select
                label={t("Currency")}
                labelClasses={"!text-base md:!text-lg !text-deep-purple !mb-2"}
                selectedValue={values?.currency?.value}
                dropdownData={
                  polishedCountryList({
                    list: countryData?.pages?.flatMap((page) => page?.data),
                  })?.map((c) => {
                    return {
                      ...c,
                      name: (
                        <div className="flex items-center gap-2">
                          <div className="h-3 min-w-3 flex items-center justify-center bg-slate-100 rounded-md p-2 py-3">
                            {c?.currency_symbol}
                          </div>{" "}
                          {c?.currency_name} {`(${c?._id})`}
                        </div>
                      ),
                      value: c?._id,
                    };
                  }) || [{ name: "Select currency", value: "" }]
                }
                isDisabled={data?.published}
                placeholder="Select a currency"
                dropdownClass="w-full"
                dropdownButtonClass={classNames(
                  "!border-surface-05 !rounded-md !bg-surface-04 !h-12 !font-medium !text-deep-purple w-full",
                  data?.published ? "opacity-50 cursor-not-allowed" : ""
                )}
                optionsClass="hover:bg-primary/10"
                onChange={(_, item) => {
                  delete item?.name;
                  handleChange({
                    target: {
                      value: {
                        ...item,
                        name: item?.currency_name,
                        symbol: item?.currency_symbol,
                      },
                      name: "currency",
                    },
                  });
                }}
                isSearch
                searchValue={searchCurrency}
                onSearchHandler={setSearchCurrency}
                onScroll={(event) => {
                  if (
                    Math.ceil(event.target.scrollTop) + 70 >=
                      event.target.scrollHeight - event.target.clientHeight &&
                    hasNextPage
                  ) {
                    fetchNextPage();
                  }
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="relative">
              <DatePicker
                label={t("Pick a Date Range")}
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                placeholder={t("Select Date")}
                inputClassName={
                  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                }
                minDate={new Date()}
                range
                onChange={(val) => {
                  setFieldsValue({ start_end_date: val });
                }}
                value={{
                  startDate: values?.start_end_date?.startDate,
                  endDate: values?.start_end_date?.endDate,
                }}
                {...formValidate(errors, "start_end_date")}
              />
            </div>
            <div className="relative">
              <TimePicker
                label={t("Start Time")}
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                inputClassName={
                  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                }
                value={values?.startTime}
                onChange={(time) => {
                  handleChange({
                    target: { name: "startTime", value: time },
                  });
                }}
                {...formValidate(errors, "startTime")}
              />
            </div>
            <div className="relative">
              <TimePicker
                label={t("End Time")}
                labelClasses={"!text-lg !text-deep-purple !mb-2"}
                inputClassName={
                  "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                }
                value={values?.endTime}
                onChange={(time) => {
                  handleChange({
                    target: { name: "endTime", value: time },
                  });
                }}
                {...formValidate(errors, "endTime")}
              />
            </div>
          </div>
          {values?.locationStatus === "offline" && (
            <div className="relative">
              <LocationSearch
                value={values?.address?.name || ""}
                onLocationSelect={(val) => {
                  handleChange({
                    target: {
                      name: "address",
                      value: {
                        ...val?.address,
                        name: val?.display_name,
                        coordinates: [val?.lat, val?.lon],
                      },
                    },
                  });
                }}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default Venue;
